import { writable, readable, derived } from 'svelte/store';
export class SelectionRange {
    constructor(minDate, minStay, singleDate = false) {
        this.minDate = minDate;
        this.minStay = minStay;
        this.singleDate = singleDate;
        this.range = new Range();
        this.shadowRange = new ShadowRange(this.range);
        this.change = writable(null);
    }
    get value() {
        return derived(this.range.value, (value, set) => {
            if (this.reverse)
                set([...value].reverse());
            else
                set(value);
        });
    }
    set(from, to) {
        this.range.clear();
        this.shadowRange.clear();
        this.reverse = (from === null || from === void 0 ? void 0 : from.ticks) > (to === null || to === void 0 ? void 0 : to.ticks);
        if (from) {
            this.select(from);
        }
        to = to || from;
        if (to) {
            this.select(to);
        }
    }
    pickFromDate() {
        const to = this.range.to;
        this.range.clear();
        this.shadowRange.clear();
        if (to) {
            this.select(to);
        }
    }
    pickToDate() {
        const from = this.range.from;
        this.range.clear();
        this.shadowRange.clear();
        if (from) {
            this.select(from);
        }
    }
    mouseenter(date) {
        this.shadowRange.setEnd(date);
        this.change.set({});
    }
    mouseleave(date) {
        this.shadowRange.clearEnd(date);
        this.change.set({});
    }
    select(date) {
        this.range.select(date);
        this.shadowRange.setEnd(date);
        if (this.singleDate) {
            this.range.select(date);
            this.shadowRange.setEnd(date);
        }
        this.change.set({});
    }
    update() {
        this.range.update();
        this.change.set({});
    }
    clear() {
        this.range.clear();
        this.shadowRange.clear();
    }
    getState(date) {
        return this.onChange(() => {
            return {
                contains: date ? this.contains(date) : null,
                containsShadow: date ? this.shadowRange.contains(date) : null,
                isStart: date ? this.isStart(date) : null,
                isEnd: date ? this.isEnd(date) : null,
                isSingle: date ? this.isSingle(date) : null,
                from: this.range.from,
                to: this.range.to,
                complete: this.range.complete,
                empty: this.range.empty,
                isDisabled: date ? this.isDisabled(date) : null,
            };
        });
    }
    isDisabled(date) {
        return this.minDate
            ? date.ticks < this.minDate.ticks
            : false;
    }
    contains(date) {
        return (this.range.contains(date) ||
            this.shadowRange.contains(date));
    }
    isStart(date) {
        if (this.shadowRange.complete) {
            return this.shadowRange.isStart(date);
        }
        return this.range.isStart(date);
    }
    isEnd(date) {
        if (this.shadowRange.complete) {
            return this.shadowRange.isEnd(date);
        }
        return this.range.isEnd(date);
    }
    isSingle(date) {
        if (this.shadowRange.complete) {
            return this.shadowRange.isSingle(date);
        }
        return this.range.isSingle(date);
    }
    onChange(start) {
        return readable(start(), set => this.change.subscribe(_ => set(start())));
    }
}
export class Range {
    constructor() {
        this.valueSetter = writable([null, null]);
        this.value = this.valueSetter;
        this.from = null;
        this.to = null;
    }
    get complete() { return !!(this.from && this.to); }
    get empty() { return !this.from && !this.to; }
    select(date) {
        if (!this.from) {
            this.from = date;
            return;
        }
        if (this.complete) {
            this.from = date;
            this.to = null;
            return;
        }
        if (date.ticks < this.from.ticks) {
            const _ = !this.to ? this.from : null;
            this.from = date;
            this.to = _;
        }
        else {
            this.to = date;
        }
        if (this.complete) {
            const from = this.from;
            const to = this.to;
            this.valueSetter.set([from, to]);
        }
    }
    update() {
        this.valueSetter.set([this.from, this.to]);
    }
    contains(date) {
        if (this.complete) {
            return (date.ticks >= this.from.ticks &&
                date.ticks <= this.to.ticks);
        }
        return false;
    }
    isStart(date) {
        var _a;
        return date.ticks === ((_a = this.from) === null || _a === void 0 ? void 0 : _a.ticks);
    }
    isEnd(date) {
        var _a;
        return date.ticks === ((_a = this.to) === null || _a === void 0 ? void 0 : _a.ticks);
    }
    isSingle(date) {
        if (this.from && this.to) {
            return (date.ticks === this.from.ticks &&
                date.ticks === this.to.ticks);
        }
        else if (this.from) {
            return (date.ticks === this.from.ticks);
        }
        else if (this.to) {
            return (date.ticks === this.to.ticks);
        }
        return false;
    }
    clear() {
        this.from = null;
        this.to = null;
    }
}
export class ShadowRange extends Range {
    constructor(range) {
        super();
        this.range = range;
    }
    get complete() { return super.complete && !this.range.complete; }
    setEnd(date) {
        var _a;
        if (date.ticks < ((_a = this.range.from) === null || _a === void 0 ? void 0 : _a.ticks)) {
            this.from = date;
            this.to = this.range.from;
        }
        else {
            this.from = this.range.from;
            this.to = date;
        }
        if (!this.to) {
            this.to = this.from;
        }
    }
    clearEnd(date) {
        var _a, _b;
        if (((_a = this.from) === null || _a === void 0 ? void 0 : _a.ticks) === date.ticks ||
            ((_b = this.to) === null || _b === void 0 ? void 0 : _b.ticks) === date.ticks) {
            this.clear();
        }
    }
    contains(date) {
        return !this.range.complete && super.contains(date);
    }
    isStart(date) {
        return !this.range.complete && super.isStart(date);
    }
    isEnd(date) {
        return !this.range.complete && super.isEnd(date);
    }
    isSingle(date) {
        return !this.range.complete && super.isSingle(date);
    }
}
